import { useState, useEffect, useCallback } from "react";
import axiosInstance from "@/utils/appsettings";
import { useToast } from "@/hooks/use-toast";

export interface UniversalKeyData {
    key_id: string;
    key_prefix: string;
    key_value?: string; // Only present on creation/regeneration
    key_display: string;
    is_active: boolean;
    provider_openai: boolean;
    provider_anthropic: boolean;
    created_at: string | null;
    last_used_at: string | null;
    regenerated_at: string | null;
    is_new?: boolean;
}

// Shared by the Universal AI Key modal and page; loads (and resets reveal/confirm state) whenever `active` turns on
export function useUniversalKey(active: boolean) {
    const [keyData, setKeyData] = useState<UniversalKeyData | null>(null);
    const [loading, setLoading] = useState(false);
    const [copied, setCopied] = useState(false);
    const [showRegenConfirm, setShowRegenConfirm] = useState(false);
    const [showFullKey, setShowFullKey] = useState(false);
    const [providerLoading, setProviderLoading] = useState<string | null>(null);
    const [isRegenerating, setIsRegenerating] = useState(false);
    const [isToggling, setIsToggling] = useState(false);
    const { toast } = useToast();

    const fetchKeyData = useCallback(async () => {
        setLoading(true);
        try {
            const response = await axiosInstance.get("/universal-key/");
            const data = response.data;
            setKeyData(data);
            if (data.is_new && data.key_value) {
                setShowFullKey(true);
            }
        } catch (error) {
            toast({
                title: "Error",
                description: "Failed to load Universal AI Key",
                variant: "destructive",
            });
        } finally {
            setLoading(false);
        }
    }, [toast]);

    useEffect(() => {
        if (active) {
            fetchKeyData();
            setShowFullKey(false);
            setShowRegenConfirm(false);
        }
    }, [active, fetchKeyData]);

    const handleCopy = async () => {
        if (!keyData) return;
        const textToCopy = keyData.key_value || keyData.key_display;
        await navigator.clipboard.writeText(textToCopy);
        setCopied(true);
        toast({ title: "Copied!", description: "Key copied to clipboard" });
        setTimeout(() => setCopied(false), 2000);
    };

    const handleRegenerate = async () => {
        setIsRegenerating(true);
        try {
            const response = await axiosInstance.post("/universal-key/regenerate");
            const data = response.data;
            setKeyData(data);
            setShowFullKey(true);
            setShowRegenConfirm(false);
            toast({
                title: "Key Regenerated",
                description: "Your old key has been invalidated. Save your new key.",
            });
        } catch (error) {
            toast({
                title: "Error",
                description: "Failed to regenerate key",
                variant: "destructive",
            });
        } finally {
            setIsRegenerating(false);
        }
    };

    const handleToggle = async () => {
        setIsToggling(true);
        try {
            const response = await axiosInstance.post("/universal-key/toggle");
            setKeyData((prev) =>
                prev ? { ...prev, is_active: response.data.is_active } : null
            );
            toast({
                title: response.data.is_active ? "Key Enabled" : "Key Disabled",
                description: response.data.message,
            });
        } catch (error) {
            toast({
                title: "Error",
                description: "Failed to toggle key",
                variant: "destructive",
            });
        } finally {
            setIsToggling(false);
        }
    };

    const handleProviderToggle = async (
        provider: "openai" | "anthropic",
        value: boolean
    ) => {
        setProviderLoading(provider);
        try {
            const response = await axiosInstance.put("/universal-key/providers", {
                [provider]: value,
            });
            setKeyData((prev) =>
                prev
                    ? {
                        ...prev,
                        provider_openai: response.data.openai,
                        provider_anthropic: response.data.anthropic,
                    }
                    : null
            );
        } catch (error) {
            toast({
                title: "Error",
                description: "Failed to update provider",
                variant: "destructive",
            });
        } finally {
            setProviderLoading(null);
        }
    };

    const displayKey =
        showFullKey && keyData?.key_value
            ? keyData.key_value
            : keyData?.key_display || (loading ? "Loading..." : "");

    return {
        keyData,
        loading,
        copied,
        showRegenConfirm,
        setShowRegenConfirm,
        showFullKey,
        setShowFullKey,
        providerLoading,
        isRegenerating,
        isToggling,
        displayKey,
        handleCopy,
        handleRegenerate,
        handleToggle,
        handleProviderToggle,
    };
}
